import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../services/analytics';
import { TOUR_STEPS, getStepIndex, type TourStepId, type TipKey } from '../services/tour';

const STORAGE_KEY = 'parksy_tour_seen_v1';
const STORAGE_VERSION = 1;
const AUTO_START_DELAY_MS = 1200;

type Status = 'never' | 'completed' | 'dismissed';

interface PersistedState {
  v: number;
  status: Status;
  tips_seen: Partial<Record<TipKey, true>>;
}

type TourRunState =
  | { status: 'idle' }
  | { status: 'running'; stepIndex: number };

function readState(): PersistedState {
  if (typeof window === 'undefined') {
    return { v: STORAGE_VERSION, status: 'never', tips_seen: {} };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { v: STORAGE_VERSION, status: 'never', tips_seen: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== STORAGE_VERSION) {
      return { v: STORAGE_VERSION, status: 'never', tips_seen: {} };
    }
    return {
      v: STORAGE_VERSION,
      status: parsed.status ?? 'never',
      tips_seen: parsed.tips_seen ?? {},
    };
  } catch {
    return { v: STORAGE_VERSION, status: 'never', tips_seen: {} };
  }
}

function writeState(next: PersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    return;
  }
}

function setStatus(status: Status): void {
  const current = readState();
  writeState({ ...current, status });
}

function markTipSeenInternal(key: TipKey): boolean {
  const current = readState();
  if (current.tips_seen[key]) return false;
  writeState({ ...current, tips_seen: { ...current.tips_seen, [key]: true } });
  return true;
}

export function resetTourStateForDev(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function useTour() {
  const [run, setRun] = useState<TourRunState>({ status: 'idle' });
  const runRef = useRef(run);
  runRef.current = run;

  const start = useCallback((fromStep: number = 0) => {
    const safe = Math.max(0, Math.min(fromStep, TOUR_STEPS.length - 1));
    setRun({ status: 'running', stepIndex: safe });
    trackEvent('tour_started', {});
  }, []);

  const next = useCallback(() => {
    const current = runRef.current;
    if (current.status !== 'running') return;
    const step = TOUR_STEPS[current.stepIndex];
    if (step) {
      trackEvent('tour_step_completed', { step_id: step.id });
    }
    if (current.stepIndex >= TOUR_STEPS.length - 1) {
      trackEvent('tour_completed', { step_count: TOUR_STEPS.length });
      setStatus('completed');
      setRun({ status: 'idle' });
      return;
    }
    const nextIndex = current.stepIndex + 1;
    const nextStep = TOUR_STEPS[nextIndex];
    if (nextStep?.beforeShow) nextStep.beforeShow();
    setRun({ status: 'running', stepIndex: nextIndex });
  }, []);

  const prev = useCallback(() => {
    const current = runRef.current;
    if (current.status !== 'running') return;
    if (current.stepIndex <= 0) return;
    setRun({ status: 'running', stepIndex: current.stepIndex - 1 });
  }, []);

  const dismiss = useCallback(() => {
    const current = runRef.current;
    if (current.status !== 'running') return;
    const step = TOUR_STEPS[current.stepIndex];
    if (step) {
      trackEvent('tour_dismissed', {
        at_step_id: step.id,
        step_index: current.stepIndex,
      });
    } else {
      trackEvent('tour_step_skipped', { step_id: 'unknown' });
    }
    setStatus('dismissed');
    setRun({ status: 'idle' });
  }, []);

  const skipStep = useCallback(() => {
    const current = runRef.current;
    if (current.status !== 'running') return;
    const step = TOUR_STEPS[current.stepIndex];
    if (step) {
      trackEvent('tour_step_skipped', { step_id: step.id });
    }
    next();
  }, [next]);

  const isRunning = run.status === 'running';
  const currentStep = isRunning ? TOUR_STEPS[run.stepIndex] : null;

  const goToStep = useCallback((id: TourStepId) => {
    const idx = getStepIndex(id);
    if (idx < 0) return;
    setRun({ status: 'running', stepIndex: idx });
  }, []);

  return {
    isRunning,
    currentStep,
    stepIndex: run.status === 'running' ? run.stepIndex : -1,
    totalSteps: TOUR_STEPS.length,
    start,
    next,
    prev,
    dismiss,
    skipStep,
    goToStep,
  };
}

export function useAutoStartTour(enabled: boolean, isMapReady: boolean, startFn: (from?: number) => void) {
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    if (!enabled) return;
    if (!isMapReady) return;
    const persisted = readState();
    if (persisted.status !== 'never') return;
    startedRef.current = true;
    const timer = window.setTimeout(() => {
      startFn(0);
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, isMapReady, startFn]);
}

export function useTourStatus(): Status {
  const [status, setStatusState] = useState<Status>(() => readState().status);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setStatusState(readState().status);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return status;
}

export function useTipSeen(key: TipKey): { seen: boolean; mark: () => void } {
  const [seen, setSeen] = useState<boolean>(() => Boolean(readState().tips_seen[key]));
  const mark = useCallback(() => {
    if (markTipSeenInternal(key)) setSeen(true);
  }, [key]);
  return { seen, mark };
}
